import styled from 'styled-components';
import { Card } from 'antd';
import { Link } from 'react-router-dom';

interface StyledCardProps {
  token: any;
  minHeight?: string;
}

export const StyledLinkButton = styled(Link)`
  text-decoration: none;
  color: inherit;
  display: block;
  width: 100%;
  transition: transform 0.25s ease;

  &:hover {
    transform: translateY(-6px);
  }
`;

export const StyledCard = styled(Card)<StyledCardProps>`
  border: none;
  border-radius: 14px;
  overflow: hidden;
  position: relative;
  width: 100%;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.18);
  transition: box-shadow 0.3s ease;
  height: 100%;
  min-height: ${(props) => props.minHeight || '142px'};

  background: ${({ token }) => {
    const color = token.colorPrimary;
    const isLilacTheme = color?.toLowerCase() === '#9a97d6';

    if (isLilacTheme) {
      return 'linear-gradient(135deg, #9A97D6 0%, #7F7BC4 50%, #B8B5E8 100%)';
    }
    return token.colorPrimary;
  }};

  &::before {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 48px;
    height: 48px;
    background: ${({ token }) => {
      const color = token.colorPrimary;
      const isBluishTheme = color?.toLowerCase() === '#3c5161';
      const isLilacTheme = color?.toLowerCase() === '#9a97d6';
      const isLightTheme = color?.toLowerCase() === '#57719e';

      if (color === '#1b1860') {
        return 'linear-gradient(307deg, rgba(12, 111, 182, 0.7) 50%, rgba(1, 81, 122, 0.6) 75%, rgba(24, 63, 98, 0.8) 100%)';
      } else if (color === '#053170') {
        return 'linear-gradient(307deg, rgba(40, 77, 186, 0.7) 50%, rgba(23, 64, 117, 0.6) 75%, rgba(13, 51, 111, 0.8) 100%)';
      } else if (color === '#44a7fb') {
        return 'linear-gradient(307deg, rgba(4, 101, 139, 0.7) 50%, rgba(27, 189, 230, 0.6) 75%, rgba(21, 192, 244, 0.8) 100%)';
      } else if (color === '#c065a2') {
        return 'linear-gradient(307deg, rgba(160, 72, 116, 0.7) 50%, rgba(243, 99, 192, 0.6) 75%, rgba(174, 69, 151, 0.8) 100%)';
      } else if (color === '#2e8b57') {
        return 'linear-gradient(307deg, rgba(6, 148, 37, 0.7) 50%, rgba(89, 202, 87, 0.6) 75%, rgba(57, 224, 110, 0.8) 100%)';
      } else if (isLilacTheme) {
        return 'linear-gradient(307deg, rgba(154, 151, 214, 0.7) 50%, rgba(127, 123, 196, 0.7) 75%, rgba(184, 181, 232, 0.65) 100%)';
      } else if (isBluishTheme) {
        return 'linear-gradient(307deg, rgb(172 211 223 / 90%) 50%, rgb(130 157 176 / 80%) 75%, rgb(78 79 82) 100%)';
      } else if (isLightTheme) {
        return 'linear-gradient(307deg, rgb(255 251 255) 50%, rgba(55, 108, 200, 0.5) 75%, rgba(55, 108, 200, 0.7) 100%)';
      } else {
        return `linear-gradient(307deg, ${color}99 50%, ${color}80 75%, ${color}B3 100%)`;
      }
    }};
    clip-path: polygon(0 0, 100% 0, 0 100%);

    box-shadow: ${({ token }) => {
      const color = token.colorPrimary;
      const isBluishTheme = color?.toLowerCase() === 'rgb(87 113 158)';
      const isLilacTheme = color?.toLowerCase() === '#9a97d6';

      if (color === '#1b1860') {
        return '0 0 10px rgba(255, 193, 7, 0.5)';
      } else if (color === '#053170') {
        return '0 0 10px rgba(255, 140, 0, 0.5)';
      } else if (color === '#44a7fb') {
        return '0 0 10px rgba(255, 127, 80, 0.5)';
      } else if (color === '#c065a2') {
        return '0 0 10px rgba(152, 251, 152, 0.5)';
      } else if (color === '#2e8b57') {
        return '0 0 10px rgba(250, 128, 114, 0.5)';
      } else if (isLilacTheme) {
        return '0 0 10px rgba(154, 151, 214, 0.6)';
      } else if (isBluishTheme) {
        return '0 0 15px rgba(0, 191, 255, 0.8)';
      } else {
        return `0 0 10px ${color}4D`;
      }
    }};
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 6px;
    background: ${({ token }) => {
      const color = token.colorPrimary;
      const isBluishTheme = color?.toLowerCase() === 'rgb(87 113 158)';
      const isLilacTheme = color?.toLowerCase() === '#9a97d6';

      if (color === '#1b1860') {
        return 'linear-gradient(180deg, rgba(12, 111, 182, 0.8), rgba(24, 63, 98, 0.9))';
      } else if (color === '#053170') {
        return 'linear-gradient(180deg, rgba(40, 77, 186, 0.8), rgba(13, 51, 111, 0.9))';
      } else if (color === '#44a7fb') {
        return 'linear-gradient(180deg, rgba(4, 101, 139, 0.8), rgba(21, 192, 244, 0.9))';
      } else if (color === '#c065a2') {
        return 'linear-gradient(180deg, rgba(160, 72, 116, 0.8), rgba(174, 69, 151, 0.9))';
      } else if (color === '#2e8b57') {
        return 'linear-gradient(180deg, rgba(6, 148, 37, 0.8), rgba(57, 224, 110, 0.9))';
      } else if (isLilacTheme) {
        return 'linear-gradient(180deg, rgba(154, 151, 214, 0.8), rgba(127, 123, 196, 0.7))';
      } else if (isBluishTheme) {
        return 'linear-gradient(180deg, rgba(0, 191, 255, 0.9), rgba(65, 105, 225, 1))';
      } else {
        return `linear-gradient(180deg, ${color}CC, ${color}E6)`;
      }
    }};
    border-radius: 0 20px 20px 0;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.3);
  }
  .ant-card-body {
    padding: 40px 16px 20px 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;

    @media (max-width: 480px) {
      padding: 28px 12px 14px;
    }

    @media (max-width: 380px) {
      padding: 20px 10px 10px;
    }
  }
  span {
    font-size: 16px;
    font-weight: 600;
    margin-top: 0;
    color: white;
    text-align: center;
    padding-bottom: 6px;
    text-transform: capitalize;
    @media (max-width: 1400px) {
      font-size: 14px;
    }
    @media (max-width: 480px) {
      font-size: 13px;
    }
    @media (max-width: 380px) {
      font-size: 12px;
    }
  }
`;

export const IconWrapper = styled.div<{ token: any }>`
  background: linear-gradient(
    135deg,
    ${({ token }) => token.colorPrimary},
    #2563eb
  );
  border-radius: 50%;
  padding: 24px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 48px;
    height: 48px;
    color: #ffffff;
  }
`;

export const StyledMeta = styled(Card.Meta)<{ token: any }>`
  .ant-card-meta-title {
    font-size: 15px;
    font-weight: 600;
    color: #ffffff;
    margin-bottom: 4px;
    text-align: center;
    white-space: pre-line !important;

    @media (max-width: 1400px) {
      font-size: 13px;
    }
  }

  .ant-card-meta-description {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.85);
    text-align: center;
    line-height: 1.35;
  }
`;
